import React, { useMemo, useCallback, useEffect, } from 'react'

import {
  Row,
  Menu,
  Dropdown,
  Button,
  Space,
  Tooltip, Select
} from 'antd'

import {
  DownOutlined,
  VerticalAlignBottomOutlined,
  CaretDownOutlined,
  CaretUpOutlined,
  UngroupOutlined,
  GroupOutlined
} from '@ant-design/icons'

import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint'

import { VIEW } from '../../hooks'

import {vec3} from 'gl-matrix'

import './VTKFloatingToolbar.css'
import { VTKViewDropdown } from 'components/VTKViewDropdown'

type DataSetGroup = {
  name: string, 
  colorBy: string, 
  id: string, 
  data: DataSet[] 
}

type DataSet = {
  name: string, 
  range: [number, number]
}

interface VTKFloatingToolbarProps {
  dispatch?: any
  viewerState?: any
  handleScreenshot?: () => void
  collapsed?: boolean
}

const views = [
  {
    label: 'Top',
    key: 'top',
    icon: <VerticalAlignBottomOutlined />
  },
  {
    label: 'Bottom',
    key: 'bottom',
    icon: <VerticalAlignBottomOutlined rotate={180} />
  },
  {
    label: 'Left',
    key: 'left',
    icon: <VerticalAlignBottomOutlined rotate={90} />
  },
  {
    label: 'Right',
    key: 'right',
    icon: <VerticalAlignBottomOutlined rotate={270} />
  },
  {
    label: 'Front',
    key: 'front',
    icon: <CaretUpOutlined />
  },
  {
    label: 'Back',
    key: 'back',
    icon: <CaretDownOutlined />
  },
]

// setParentState and config are basically just for Streamlit
// a cleaner approach would be to reimpllement VTKPollination as the streamlit component
export const VTKFloatingToolbar: React.FunctionComponent<VTKFloatingToolbarProps> = ({ dispatch, viewerState, handleScreenshot, collapsed }) => {
  
  const { xs } = useBreakpoint()
  
  const selectedView = useMemo(() => {
    if (!viewerState || !viewerState.renderer || !viewerState.renderer.camera) return undefined
    const { directionOfProjection, viewUp } = viewerState.renderer.camera

    const cameraDirection = vec3.create()
    const [dx, dy, dz] = directionOfProjection
    const dP = vec3.fromValues(dx, dy, dz)
    vec3.scale(cameraDirection, dP, -1)

    const [cux, cuy, cuz] = viewUp
    const cameraUp = vec3.fromValues(cux, cuy, cuz)
    
    for (const view of Object.keys(VIEW)) {
      const [cx, cy, cz] = VIEW[view].direction
      const viewDirection = vec3.fromValues(cx, cy, cz)
      const [ux, uy, uz] = VIEW[view].up
      const up = vec3.fromValues(ux, uy, uz)
      if (vec3.equals(cameraDirection, viewDirection) && vec3.equals(cameraUp, up)) return view
    }
    return undefined
  }, [viewerState])

  const dataSets = useMemo(() => {
    if (!viewerState || !viewerState.scene || !viewerState.scene.length) return []
    return viewerState.scene.reduce((p, item) => {
      const mapper  = item.state.mapper
      const inputData = mapper.inputData
      const data = [...inputData.cellData.arrays, ...inputData.pointData.arrays]
      if ( data.length === 0 ) return p
      return [...p, {name: item.name, id: item.id , colorBy: mapper.colorByArrayName, data}]
    }, [])
  }, [viewerState])

  const dataIsolated = useMemo(() => {
    if (!viewerState || !viewerState.scene || !viewerState.scene.length) return false
    return viewerState.scene.reduce((p, item) => {
      const inputData  = item.state.mapper.inputData
      const data = [...inputData.cellData.arrays, ...inputData.pointData.arrays]
      const hasData = data.length > 0
      // if hasData and is visibible, true
      // if hasData and is not visibile, false
      // if not hasData and is visible, false
      // if not hasData and is not visibile, true
      return p && ((hasData && item.state.visibility) || (!hasData && !item.state.visibility))
    }, true)
  }, [viewerState])

  const handleViewSelect = useCallback(({ key }: any) => {
    if (!dispatch) return
    dispatch({ type: 'select-view', value: key })
  },[dispatch])

  const handleIsolateData = useCallback((bool: boolean) => {
    if (!dispatch) return
    dispatch({ type: 'isolate-data', value: bool })
  }, [dispatch])

  if ( dataSets.length === 0 ) {
    return (
      <Row justify='center'
        align='middle'
        style={{ height: 0, zIndex: 1049 }}
      > 
        <Tooltip title='Select View'>
          <Dropdown overlay={
            <Menu onClick={handleViewSelect}
              selectedKeys={[selectedView]}
              style={{width:'100%', padding:4}}
            >
              {
                views.map((item) => (
                  <Menu.Item key={item.key}
                    icon={item.icon}
                    style={{margin:'2px 0px', borderRadius:4}}
                  >
                    {item.label}
                  </Menu.Item>
                ))
              }
            </Menu>
          }
          trigger={['click']}
          key='p-views-dropdown'
          >
            <Button style={{width:'100%', maxWidth: 150, zIndex: 1 }}
              className='p-float'
            >
              {selectedView ? selectedView.charAt(0).toUpperCase() + selectedView.slice(1) : 'Views'} 
              <DownOutlined />
            </Button>
          </Dropdown>
        </Tooltip>
      </Row>
    )
  }

  return (
    <Row justify='center'
      align='middle'
      style={{ height: 0, zIndex: 1049 }}
    >      
      { xs || collapsed  ?
        <VTKViewDropdown viewerState={viewerState}
          dispatch={dispatch}
        />
        :
        <Space className='p-float p-viewer-floating-menu'
          size={24}
          style={{ zIndex: 1 }}
        >
          <Tooltip title='Select Dataset'>
            <Select value={dataSets.map(d => `${d.colorBy}::${d.id}`)}
              style={{ minWidth: '175px' }} 
              dropdownStyle={{ padding: '2px 4px' }}
              placement='bottomLeft'
              dropdownMatchSelectWidth={false}
              onChange={(value: string, option) => {
                const [colorBy, id] = value.split('::')
                dispatch({ type: 'color-by', ids: [id], value: colorBy })
              }}
            >
              { dataSets.map(( dataSet: DataSetGroup) =>
                <Select.OptGroup 
                  label={dataSet.name} 
                  key={`${dataSet.name.toLocaleLowerCase().replace(' ', '-')}::${dataSet.id}`}
                >
                  { dataSet.data.map((data: DataSet) => 
                    <Select.Option 
                      value={`${data.name}::${dataSet.id}`}
                      key={`${data.name.toLocaleLowerCase().replace(' ', '-')}::${dataSet.id}`} 
                      style={{
                        margin: '2px 0px', 
                        borderRadius: 4
                      }}
                    >
                      {data.name}
                    </Select.Option>)}
                </Select.OptGroup>
              )}
            </Select>
          </Tooltip>
          <Tooltip title={dataIsolated ? 'Show All' : 'Isolate Data'}>
            <Button shape='circle'
              onClick={() => handleIsolateData(dataIsolated)}
            >
              {dataIsolated ? <GroupOutlined/> : <UngroupOutlined/> }
            </Button>
          </Tooltip>
          <VTKViewDropdown viewerState={viewerState}
            dispatch={dispatch}
            inline={true}
          />
        </Space>
      }
    </Row>
  )
}